import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, EyeOff, Eye } from 'lucide-react';
import { usePatient, useDecryptCpr } from '@/hooks/usePatients';
import { usePatientAppointments } from '@/hooks/usePatientAppointments';
import { toast } from 'react-hot-toast';

interface PatientStripProps {
  patientId: string;
}

/** Format a date string as a short Danish date, e.g. "14. apr 2026" */
function formatDanishDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

export function PatientStrip({ patientId }: PatientStripProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: patient, isLoading } = usePatient(patientId);
  const { data: appointments } = usePatientAppointments(patientId);
  const decryptCprMutation = useDecryptCpr();
  const [revealedCpr, setRevealedCpr] = useState<string | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);

  const fullName = patient
    ? `${patient.first_name} ${patient.last_name}`
    : '—';

  // Last appointment (already sorted desc from hook)
  const lastAppointment = appointments?.[0] ?? null;
  const lastSeenText = lastAppointment
    ? formatDanishDate(lastAppointment.starts_at)
    : '—';

  const handleRevealCpr = async () => {
    if (revealedCpr) {
      setRevealedCpr(null);
      return;
    }
    setIsRevealing(true);
    try {
      const result = await decryptCprMutation.mutateAsync({
        patient_id: patientId,
        source: 'dental_module_patient_strip',
        purpose: 'dental_strip',
      });
      setRevealedCpr(result.cpr);
    } catch {
      toast.error('CPR kunne ikke hentes');
    } finally {
      setIsRevealing(false);
    }
  };

  const cprDisplay = revealedCpr
    ? revealedCpr
    : 'XXXXXX-XXXX';

  const patientUrl = `/patients/${patientId}`;

  return (
    <div className="sticky top-0 z-30 flex h-14 w-full items-center gap-4 border-b border-ds-line bg-ds-card px-4 shadow-card">
      {/* Back button */}
      <button
        onClick={() => navigate(patientUrl)}
        className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text hover:bg-ds-row-hover"
        aria-label={t('dental.module.backToPatient')}
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="hidden sm:inline">{t('dental.module.backToPatient')}</span>
      </button>

      <div className="h-5 w-px shrink-0 bg-ds-neutral-tint" />

      {/* Patient name — opens in new tab */}
      <a
        href={patientUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 font-semibold text-ds-text hover:text-ds-teal"
      >
        {isLoading ? '…' : fullName}
      </a>

      {/* TODO: medical alerts when patients.medical_alerts lands (out of scope for TAND-2) */}

      {/* Spacer */}
      <div className="flex-1" />

      {/* CPR */}
      {patient?.has_cpr && (
        <div className="hidden items-center gap-1.5 md:flex">
          <span className="font-mono text-sm text-ds-muted">
            {cprDisplay}
          </span>
          <button
            onClick={handleRevealCpr}
            disabled={isRevealing}
            title={
              revealedCpr
                ? 'Skjul CPR'
                : t('dental.module.cprMasked')
            }
            className="rounded p-0.5 text-ds-muted hover:text-ds-text disabled:opacity-50"
          >
            {revealedCpr ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </button>
        </div>
      )}

      {!patient?.has_cpr && (
        <div className="hidden items-center gap-1.5 md:flex">
          <EyeOff className="h-4 w-4 text-ds-placeholder text-ds-muted" />
          <span className="text-sm text-ds-placeholder">
            {t('dental.module.cprMasked')}
          </span>
        </div>
      )}

      {/* Last seen */}
      <div className="hidden items-center gap-1.5 lg:flex">
        <span className="text-xs text-ds-placeholder">
          {t('dental.module.lastSeen')}:
        </span>
        <span className="text-sm text-ds-muted">
          {lastSeenText}
        </span>
      </div>
    </div>
  );
}
